import React, { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import api, { formatErr } from "../../../lib/api";
import { card, label, btn, btnGhost } from "../drawings/_drawingsCommon";
import SyncStatusBadge from "./SyncStatusBadge";

export default function ModelEditor3D({ projectId }) {
  const mountRef = useRef();
  const stateRef = useRef({ scene: null, camera: null, renderer: null, equipMap: new Map() });
  const [palette, setPalette] = useState([]);
  const [serviceColors, setServiceColors] = useState({});
  const [scene, setScene] = useState(null);
  const [selected, setSelected] = useState(null);
  const [pendingPlace, setPendingPlace] = useState(null);
  const [err, setErr] = useState("");

  // ─── Load data ─────────────────────────────────────────────────────
  const load = async () => {
    try {
      const [pl, sc] = await Promise.all([
        api.get(`/projects/${projectId}/model/palette`),
        api.get(`/projects/${projectId}/model/scene`),
      ]);
      setPalette(pl.data.palette);
      setServiceColors(pl.data.service_colors);
      setScene(sc.data);
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
  };
  useEffect(() => { load(); }, [projectId]);

  // ─── Three.js init ─────────────────────────────────────────────────
  useEffect(() => {
    if (!mountRef.current) return;
    const w = mountRef.current.clientWidth, h = 600;
    const s = new THREE.Scene();
    s.background = new THREE.Color(0x0d0f12);
    const camera = new THREE.PerspectiveCamera(50, w / h, 0.1, 1000);
    camera.position.set(40, 30, 40);
    camera.lookAt(0, 5, 0);
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(w, h);
    mountRef.current.innerHTML = "";
    mountRef.current.appendChild(renderer.domElement);

    s.add(new THREE.GridHelper(80, 40, 0x444444, 0x222222));
    s.add(new THREE.AxesHelper(10));

    const ambient = new THREE.AmbientLight(0xffffff, 0.6);
    const dir = new THREE.DirectionalLight(0xffffff, 0.8);
    dir.position.set(20, 30, 20);
    s.add(ambient); s.add(dir);

    // Raycaster + invisible ground plane for drag
    const raycaster = new THREE.Raycaster();
    const ground = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
    const mouse = new THREE.Vector2();
    let drag = false, lastX = 0, lastY = 0, theta = Math.PI / 4, phi = Math.PI / 4, R = 70;
    let draggingMesh = null;
    let draggingId = null;

    const updateCam = () => {
      camera.position.set(R * Math.sin(phi) * Math.cos(theta), R * Math.cos(phi),
                            R * Math.sin(phi) * Math.sin(theta));
      camera.lookAt(0, 5, 0);
    };
    updateCam();

    const _ndc = (e) => {
      const r = renderer.domElement.getBoundingClientRect();
      mouse.x = ((e.clientX - r.left) / r.width) * 2 - 1;
      mouse.y = -((e.clientY - r.top) / r.height) * 2 + 1;
    };
    const onDown = (e) => {
      _ndc(e);
      raycaster.setFromCamera(mouse, camera);
      const meshes = [...stateRef.current.equipMap.values()];
      const hit = raycaster.intersectObjects(meshes, false)[0];
      if (hit && !e.shiftKey) {
        // Pick — start dragging this equipment on the ground plane
        draggingMesh = hit.object;
        draggingId = [...stateRef.current.equipMap.entries()].find(([_, m]) => m === draggingMesh)?.[0];
      } else {
        drag = true; lastX = e.clientX; lastY = e.clientY;
      }
    };
    const onMove = (e) => {
      if (draggingMesh) {
        _ndc(e);
        raycaster.setFromCamera(mouse, camera);
        const intersect = new THREE.Vector3();
        raycaster.ray.intersectPlane(ground, intersect);
        if (intersect) {
          // Snap to 1 m grid
          draggingMesh.position.x = Math.round(intersect.x);
          draggingMesh.position.z = Math.round(intersect.z);
        }
        return;
      }
      if (!drag) return;
      theta -= (e.clientX - lastX) * 0.01;
      phi = Math.max(0.1, Math.min(Math.PI - 0.1, phi + (e.clientY - lastY) * 0.01));
      lastX = e.clientX; lastY = e.clientY;
      updateCam();
    };
    const onUp = () => {
      if (draggingMesh && draggingId) {
        // Save back to backend (three Y is up — our z is height)
        const x = draggingMesh.position.x, y = draggingMesh.position.z, z = draggingMesh.position.y;
        api.put(`/projects/${projectId}/model/equipment/${draggingId}`, { x, y, z }).catch(() => {});
      }
      draggingMesh = null; draggingId = null; drag = false;
    };
    const onWheel = (e) => { e.preventDefault(); R = Math.max(20, Math.min(200, R + e.deltaY * 0.05)); updateCam(); };
    renderer.domElement.addEventListener("mousedown", onDown);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    renderer.domElement.addEventListener("wheel", onWheel, { passive: false });

    stateRef.current = { scene: s, camera, renderer, equipMap: new Map() };

    let alive = true;
    const tick = () => {
      if (!alive) return;
      renderer.render(s, camera);
      requestAnimationFrame(tick);
    };
    tick();
    return () => {
      alive = false;
      renderer.domElement.removeEventListener("mousedown", onDown);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      renderer.domElement.removeEventListener("wheel", onWheel);
      renderer.dispose();
    };
  }, [projectId]);

  // ─── Render scene whenever state changes ───────────────────────────
  useEffect(() => {
    const st = stateRef.current;
    if (!st.scene || !scene) return;
    // Clear existing
    [...st.equipMap.values()].forEach((m) => st.scene.remove(m));
    st.equipMap.clear();
    (scene.equipment || []).forEach((e) => {
      const mesh = makeMesh(e);
      mesh.position.set(e.x || 0, e.z || 0, e.y || 0);   // y in three is up
      st.scene.add(mesh);
      st.equipMap.set(e.id, mesh);
    });
    // Pipes
    (st.scene.userData.pipes || []).forEach((p) => st.scene.remove(p));
    const pipes = (scene.pipes || []).map((p) => makePipeMesh(p, serviceColors));
    pipes.forEach((m) => st.scene.add(m));
    st.scene.userData.pipes = pipes;
    // Structural members
    (st.scene.userData.steel || []).forEach((p) => st.scene.remove(p));
    const steel = (scene.structural || []).map((m) => makeBeam(m));
    steel.forEach((m) => st.scene.add(m));
    st.scene.userData.steel = steel;
  }, [scene, serviceColors]);

  // ─── Add equipment on canvas click ─────────────────────────────────
  const place = async () => {
    if (!pendingPlace) return;
    try {
      const { data } = await api.post(`/projects/${projectId}/model/equipment`,
        { palette_id: pendingPlace.id, x: 0, y: 0, z: 0 });
      setScene((s) => ({ ...s, equipment: [...(s.equipment || []), data.equipment] }));
      setPendingPlace(null);
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
  };

  const generateRack = async () => {
    try {
      const { data } = await api.post(`/projects/${projectId}/model/structural/rack`,
        { bays: 6, bay_length_m: 6, tiers: 2, tier_heights_m: [4, 7], width_m: 6,
          origin: { x: -20, y: 0, z: 0 } });
      load();
      alert(`${data.members_added} steel members generated`);
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
  };

  const runClash = async () => {
    try {
      const { data } = await api.post(`/projects/${projectId}/model/clash/check`);
      setScene((s) => ({ ...s, clashes: data.clashes }));
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
  };

  const importFromRun = async () => {
    if (!window.confirm("Import all equipment from the latest AI run into the 3D model?\nEquipment will be auto-laid-out in a 6×N grid (12 m spacing). Existing items kept (merge mode).")) return;
    try {
      const { data } = await api.post(`/projects/${projectId}/model/import-from-run`,
        { mode: "merge" });
      load();
      alert(`Imported · ${data.summary.equipment_imported} from run · total ${data.summary.equipment_total} in 3D scene`);
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
  };

  const syncBackToRun = async () => {
    if (!window.confirm("Push current 3-D positions, dimensions and pipes back to the latest run?\nMTO and clash detection will reflect manual placement.")) return;
    try {
      const { data } = await api.post(`/projects/${projectId}/model/sync-back-to-run`,
        { mode: "merge" });
      const s = data.summary || {};
      alert(`Synced to run ${data.run_id}:\n  Equipment: ${s.equipment_synced}\n  Pipes: ${s.pipes_synced}\n  Structural members: ${s.structural_members}`);
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
  };

  const deleteEq = async (id) => {
    if (!window.confirm("Delete equipment?")) return;
    try {
      await api.delete(`/projects/${projectId}/model/equipment/${id}`);
      load();
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
  };

  return (
    <div data-testid="model-editor" style={{ display: "grid", gridTemplateColumns: "240px 1fr 280px", gap: 8 }}>
      {/* Palette */}
      <div style={{ ...card, padding: 8, overflowY: "auto", maxHeight: 700 }}>
        <div style={{ ...label, marginBottom: 4 }}>Equipment Palette</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 3 }}>
          {palette.map((p) => (
            <button key={p.id} data-testid={`me-pal-${p.id}`}
              style={{ ...btnGhost, padding: "5px 8px", textAlign: "left", fontSize: 11,
                       background: pendingPlace?.id === p.id ? "rgba(242,107,31,.20)" : "transparent",
                       borderColor: pendingPlace?.id === p.id ? "#F26B1F" : "var(--pf-border)" }}
              onClick={() => setPendingPlace(p)}>{p.label}</button>
          ))}
        </div>
        <div style={{ height: 8 }} />
        {pendingPlace && (
          <button data-testid="me-place" style={{ ...btn, width: "100%" }} onClick={place}>
            Place {pendingPlace.label} at origin
          </button>
        )}
        <div style={{ height: 12 }} />
        <button data-testid="me-import-run" style={{ ...btn, width: "100%" }} onClick={importFromRun}>📥 Import from latest run</button>
        <button data-testid="me-sync-back-run" style={{ ...btnGhost, width: "100%", marginTop: 4 }} onClick={syncBackToRun}>↗ Sync to run</button>
        <button data-testid="me-rack" style={{ ...btnGhost, width: "100%", marginTop: 6 }} onClick={generateRack}>Generate Pipe Rack</button>
        <button data-testid="me-clash" style={{ ...btnGhost, width: "100%", marginTop: 4 }} onClick={runClash}>Run Clash Check</button>
      </div>

      {/* 3D viewport */}
      <div style={{ ...card, padding: 0, overflow: "hidden" }}>
        <div style={{ display: "flex", padding: 6, gap: 6, background: "var(--pf-surface-2)", fontSize: 11, color: "var(--pf-muted)", alignItems: "center" }}>
          <span>3-D Editor — drag empty space to orbit · drag any equipment to move it (snaps to 1 m) · wheel to zoom · Equipment: {(scene?.equipment || []).length} · Pipes: {(scene?.pipes || []).length} · Steel: {(scene?.structural || []).length}</span>
          <span style={{ marginLeft: "auto" }} />
          <SyncStatusBadge projectId={projectId} editorName="model_3d_editor" />
        </div>
        <div ref={mountRef} data-testid="me-canvas" style={{ width: "100%", height: 600 }} />
      </div>

      {/* Side panel — equipment list + clash report */}
      <div style={{ ...card, padding: 8, overflowY: "auto", maxHeight: 700, display: "flex", flexDirection: "column", gap: 10 }}>
        <div>
          <div style={label}>Equipment ({(scene?.equipment || []).length})</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {(scene?.equipment || []).map((e) => (
              <div key={e.id} style={{ padding: 6, fontSize: 11, background: "var(--pf-surface-2)", borderRadius: 3,
                                        display: "flex", justifyContent: "space-between", alignItems: "center" }}
                   data-testid={`me-eq-${e.id}`}>
                <div>
                  <strong style={{ color: "#F26B1F" }}>{e.tag || e.id}</strong>
                  <div style={{ fontSize: 9, color: "var(--pf-muted)" }}>{e.palette_id} · ({e.x},{e.y},{e.z})</div>
                </div>
                <button style={{ ...btnGhost, color: "#ef4444", padding: "2px 6px", fontSize: 10 }}
                        onClick={() => deleteEq(e.id)}>×</button>
              </div>
            ))}
            {(scene?.equipment || []).length === 0 && <div style={{ color: "var(--pf-muted)", fontSize: 11 }}>No equipment yet</div>}
          </div>
        </div>

        <div>
          <div style={label}>Clash Report ({(scene?.clashes || []).length})</div>
          <div data-testid="me-clash-list" style={{ display: "flex", flexDirection: "column", gap: 3, maxHeight: 200, overflowY: "auto" }}>
            {(scene?.clashes || []).map((c) => (
              <div key={c.id} style={{ padding: 6, fontSize: 10, borderRadius: 3,
                background: c.type === "hard" ? "rgba(239,68,68,.15)" : "rgba(245,158,11,.15)",
                color: c.type === "hard" ? "#ef4444" : "#f59e0b" }}>
                <strong>{c.id} · {c.type.toUpperCase()}</strong><br />
                {c.object1} ↔ {c.object2} · Δ {c.overlap_mm} mm
              </div>
            ))}
            {(scene?.clashes || []).length === 0 && <div style={{ color: "#22c55e", fontSize: 11 }}>✓ No clashes</div>}
          </div>
        </div>

        {err && <div style={{ padding: 8, color: "#FF8888", background: "rgba(239,68,68,.1)", borderRadius: 4, fontSize: 11 }}>{err}</div>}
      </div>
    </div>
  );
}

function makeMesh(e) {
  const d = e.dimensions || {};
  const mat = new THREE.MeshStandardMaterial({ color: 0xc88455, metalness: 0.4, roughness: 0.6 });
  let geo;
  if (e.geometry === "cylinder") {
    const r = (d.diameter_m || 1) / 2;
    const h = d.height_m || d.length_m || 1;
    geo = new THREE.CylinderGeometry(r, r, h, 32);
    if (d.axis === "x") geo.rotateZ(Math.PI / 2);
  } else if (e.geometry === "sphere") {
    geo = new THREE.SphereGeometry((d.diameter_m || 1) / 2, 32, 16);
  } else if (e.geometry === "tank-cone" || e.geometry === "tank-flat") {
    const r = (d.diameter_m || 1) / 2;
    geo = new THREE.CylinderGeometry(r, r, d.height_m || 1, 32);
  } else {
    geo = new THREE.BoxGeometry(d.length_m || 1, d.height_m || 1, d.width_m || 1);
  }
  return new THREE.Mesh(geo, mat);
}

function makePipeMesh(p, serviceColors) {
  const wps = p.waypoints || [];
  const grp = new THREE.Group();
  const color = serviceColors[p.service] || serviceColors.default || "#888";
  const mat = new THREE.MeshStandardMaterial({ color });
  for (let i = 0; i < wps.length - 1; i++) {
    const a = wps[i], b = wps[i + 1];
    const A = new THREE.Vector3(a.x, a.z, a.y);
    const B = new THREE.Vector3(b.x, b.z, b.y);
    const len = A.distanceTo(B);
    if (len < 0.01) continue;
    const r = ((p.diameter_in || 4) * 0.0254) / 2;
    const geo = new THREE.CylinderGeometry(r, r, len, 16);
    const mesh = new THREE.Mesh(geo, mat);
    const mid = A.clone().add(B).multiplyScalar(0.5);
    mesh.position.copy(mid);
    mesh.lookAt(B);
    mesh.rotateX(Math.PI / 2);
    grp.add(mesh);
  }
  return grp;
}

function makeBeam(m) {
  const a = m.from, b = m.to;
  const A = new THREE.Vector3(a.x, a.z, a.y);
  const B = new THREE.Vector3(b.x, b.z, b.y);
  const len = A.distanceTo(B);
  if (len < 0.01) return new THREE.Object3D();
  const geo = new THREE.BoxGeometry(0.2, len, 0.2);
  const mat = new THREE.MeshStandardMaterial({ color: 0x666666 });
  const mesh = new THREE.Mesh(geo, mat);
  mesh.position.copy(A.clone().add(B).multiplyScalar(0.5));
  mesh.lookAt(B);
  mesh.rotateX(Math.PI / 2);
  return mesh;
}
